import EmptyState from '@components/Common/EmptyState';
import KpiTile from '@components/Common/KpiTile';
import MetricTable from '@components/Common/MetricTable';
import RecordLink from '@components/Common/RecordLink';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { chartColor } from '@components/Common/Charts/chartPalette';
import { getReferralMetricsQuery } from '@constants/graphQLQueries';
import dayjs from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { fmtMoney } from '@helpers/money';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import React, { useEffect, useState } from 'react';
import { isSuperAdmin, useCurrentUserRole } from '@components/Common/Utils/roles';
import { referrerLabel, referrerPath } from '@components/Referrals/referrer';
import { COUNT_Y_AXIS, linearSeries } from '@components/Common/Charts/chartAxes';

interface TypeStat {
  referrerType: string;
  referred: number;
  qualified: number;
}
interface MonthStat {
  month: string;
  qualified: number;
}
interface ReferrerStat {
  referrerType: string;
  referrerId: string;
  referrerName: string | null;
  referrerEmail: string | null;
  qualified: number;
  earned: number;
}
interface Metrics {
  totalReferred: number;
  totalQualified: number;
  qualifiedRate: number;
  totalEarned: number;
  totalPaid: number;
  outstanding: number;
  rewardsEarned: number;
  /** Rewards waiting for a Super Admin, their amount, and how many were turned down. */
  rewardsPendingReview?: number | null;
  pendingReview?: number | null;
  rewardsRejected?: number | null;
  currency: string;
  byReferrerType: TypeStat[];
  qualifiedOverTime: MonthStat[];
  topReferrers: ReferrerStat[];
}

/**
 * The referral programme's numbers, as a dashboard: who signed up and how many qualified, the
 * money earned/paid/outstanding, the HR-vs-job-seeker split, qualifications over time, and the
 * top referrers. The operational side -- the payouts table and the referred-seeker list -- stays
 * on the Referrals page; this is the read-only overview. One query, Super-Admin only.
 */
const ReferralInsights: React.FC = () => {
  // Marketing reads these totals too (owner, 2026-10-19) but can open only a job seeker's record:
  // staff and ambassador records are Super Admin pages, so those names are plain text for them.
  const superAdmin = isSuperAdmin(useCurrentUserRole());
  const [data, setData] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    fetchGraphQLApiData(getReferralMetricsQuery())
      .then((r) => {
        if (!live) return;
        setData(r?.data?.getReferralMetrics ?? null);
      })
      .catch(() => {
        if (live) setError(true);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  if (error) {
    return <EmptyState compact title="Referral metrics could not be loaded" body="Try again in a moment." />;
  }
  if (loading && !data) {
    return <SkeletonLoader rows={6} columns={4} />;
  }
  if (!data || data.totalReferred === 0) {
    return (
      <EmptyState
        title="No referrals yet"
        body="Once candidates sign up through a referral link, their numbers appear here."
      />
    );
  }

  const cur = data.currency || 'INR';
  const rate = `${Math.round(data.qualifiedRate * 100)}%`;
  const months = data.qualifiedOverTime ?? [];
  const types = data.byReferrerType ?? [];
  const top = data.topReferrers ?? [];

  return (
    <>
      <Box className="cc-kpi-grid">
        <KpiTile label="Signed up" value={data.totalReferred.toLocaleString('en-IN')} icon={<GroupAddOutlinedIcon />} tone="primary" />
        <KpiTile label="Qualified" value={data.totalQualified.toLocaleString('en-IN')} icon={<HowToRegOutlinedIcon />} tone="teal" />
        <KpiTile label="Qualified rate" value={rate} icon={<PercentOutlinedIcon />} tone="indigo" />
        <KpiTile label="Rewards earned" value={data.rewardsEarned.toLocaleString('en-IN')} icon={<WorkspacePremiumOutlinedIcon />} tone="amber" />
        <KpiTile label="Paid" value={fmtMoney(data.totalPaid, cur)} icon={<PaidOutlinedIcon />} tone="success" />
        <KpiTile label="Outstanding" value={fmtMoney(data.outstanding, cur)} icon={<PaymentsOutlinedIcon />} tone="danger" />
        <KpiTile
          label={`Pending review (${(data.rewardsPendingReview ?? 0).toLocaleString('en-IN')})`}
          value={fmtMoney(data.pendingReview ?? 0, cur)}
          icon={<HourglassEmptyOutlinedIcon />}
          tone="amber"
        />
      </Box>

      <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
        <CardContent>
          <Typography variant="subtitle1" className="an-chart-title">Qualifications over time</Typography>
          <Typography variant="caption" className="an-chart-note">Referrals that reached a complete profile, by month.</Typography>
          {months.length > 0 ? (
            <LineChart
              xAxis={[{ data: months.map((m) => dayjs(`${m.month}-01`).format("MMM 'YY")), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
              series={linearSeries([{ data: months.map((m) => m.qualified), label: 'Qualified', color: chartColor('--teal') }])}
              yAxis={COUNT_Y_AXIS}
              height={260}
              margin={{ top: 20, right: 16, bottom: 40, left: 40 }}
            />
          ) : (
            <EmptyState compact title="Nobody has qualified yet" />
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
        <CardContent>
          <Typography variant="subtitle1" className="an-chart-title">Signed up and qualified, by referrer</Typography>
          <Typography variant="caption" className="an-chart-note">Whether an HR or a job seeker shared the link.</Typography>
          <BarChart
            xAxis={[{ data: types.map((t) => referrerLabel(t.referrerType)), scaleType: 'band', tickLabelStyle: { fontSize: 12 } }]}
            series={[
              { data: types.map((t) => t.referred), label: 'Signed up', color: chartColor('--acc') },
              { data: types.map((t) => t.qualified), label: 'Qualified', color: chartColor('--teal') },
            ]}
            yAxis={COUNT_Y_AXIS}
            height={260}
            margin={{ top: 20, right: 16, bottom: 40, left: 40 }}
          />
        </CardContent>
      </Card>

      <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
        <CardContent>
          <Typography variant="subtitle1" className="an-chart-title">Top referrers</Typography>
          <Typography variant="caption" className="an-chart-note">By qualified referrals; earnings are what they have made so far.</Typography>
          <MetricTable<ReferrerStat>
            rows={top}
            getRowKey={(r) => `${r.referrerType}:${r.referrerId}`}
            emptyLabel="No qualified referrals yet."
            columns={[
              {
                key: 'referrer',
                header: 'Referrer',
                variant: 'name',
                render: (r) => (
                  superAdmin || r.referrerType === 'JOBSEEKER' ? (
                    <RecordLink to={referrerPath(r)}>{r.referrerName || r.referrerEmail || r.referrerId}</RecordLink>
                  ) : (
                    <span>{r.referrerName || r.referrerEmail || r.referrerId}</span>
                  )
                ),
              },
              { key: 'type', header: 'Type', align: 'left', render: (r) => referrerLabel(r.referrerType) },
              { key: 'qualified', header: 'Qualified', align: 'right', render: (r) => r.qualified.toLocaleString('en-IN'), sortValue: (r) => r.qualified },
              { key: 'earned', header: 'Earned', align: 'right', render: (r) => fmtMoney(r.earned, cur), sortValue: (r) => r.earned },
            ]}
          />
        </CardContent>
      </Card>
    </>
  );
};

export default ReferralInsights;
